/**
 * TRAC-848: turns the export model into a file in the customer's hands.
 *
 * Client-side only, by design. The embed's customers are not signed in, so a
 * `sessionId`-keyed history endpoint would be an unauthenticated read of someone
 * else's conversation (IDOR). Everything needed is already in the browser, so we
 * never ask the server.
 *
 * PDF ONLY (Miles, 2026-07-29). The lane originally shipped four formats; the
 * ruling cut it to one. The self-contained HTML renderer stays because it IS the
 * PDF — it is what the browser prints — but it is no longer offered as its own
 * download, and the Markdown and CSV renderers were removed with the menu.
 */

import type { Message } from '../../types/chat.types';
import { buildChatExport, type ChatExport } from './exportModel';
import { exportFilename } from './format';
import { renderHtml } from './renderHtml';
import { logger } from '../logger';

export type ExportOutcome =
  | { ok: true }
  | { ok: false; reason: 'print-unavailable' | 'empty' | 'failed' };

/** Save `content` as a file. Gesture-initiated, which is what keeps it allowed in the embed iframe. */
const downloadFile = (content: string, filename: string, mimeType: string): void => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoke on the next frame — revoking synchronously can cancel the download in Safari.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * PDF without a PDF library: print the self-contained HTML through the browser's
 * own engine. Costs nothing in the embed bundle (jsPDF would add ~350KB to a
 * script that loads on tirerack.com product pages) and produces better
 * typography and real text selection.
 *
 * Printed from an OFF-SCREEN IFRAME, not a popup tab. A new tab is intrusive,
 * looks like the download failed, and is the first thing a popup blocker eats.
 * The iframe is positioned off-screen at real page dimensions rather than sized
 * 0x0 — a zero-sized frame prints blank in some engines — and is removed once
 * printing finishes.
 */
const printHtml = (html: string): boolean => {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.style.cssText =
    'position:fixed;left:-10000px;top:0;width:816px;height:1056px;border:0;opacity:0;';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  const win = frame.contentWindow;
  if (!doc || !win) {
    frame.remove();
    return false;
  }

  doc.open();
  doc.write(html);
  doc.close();

  let removed = false;
  const cleanup = () => {
    if (removed) return;
    removed = true;
    frame.remove();
  };

  const run = () => {
    try {
      win.focus();
      // `afterprint` is the reliable signal; the timeout is a backstop for
      // browsers that never fire it (and prevents leaking the frame).
      win.addEventListener('afterprint', cleanup, { once: true });
      win.print();
    } catch (e) {
      logger.error('[CHAT_EXPORT] print failed:', e);
    }
    setTimeout(cleanup, 60_000);
  };

  // A written document may already be complete, so don't wait on `load` alone.
  if (doc.readyState === 'complete') setTimeout(run, 150);
  else frame.addEventListener('load', () => setTimeout(run, 150), { once: true });

  return true;
};

export interface ExportChatOptions {
  /** Injected so tests control the clock and the export stamp is deterministic. */
  now?: Date;
  /** Set when the app knows persistence degraded — surfaces as a completeness warning. */
  knownPersistenceGap?: string;
}

/** Build the model without writing a file — used by tests and by the UI's empty check. */
export const buildExport = (messages: Message[], options: ExportChatOptions = {}): ChatExport =>
  buildChatExport(messages, {
    now: options.now ?? new Date(),
    knownPersistenceGap: options.knownPersistenceGap,
  });

/** Print the conversation as a PDF. */
export function exportChat(messages: Message[], options: ExportChatOptions = {}): ExportOutcome {
  try {
    const chat = buildExport(messages, options);
    if (chat.turns.length === 0) return { ok: false, reason: 'empty' };

    if (printHtml(renderHtml(chat))) return { ok: true };

    // The print frame could not be created — the customer still gets their
    // conversation, just as a file they can open and print themselves. Never
    // leave them empty-handed.
    downloadFile(renderHtml(chat), exportFilename(chat, 'html'), 'text/html;charset=utf-8');
    return { ok: false, reason: 'print-unavailable' };
  } catch (e) {
    logger.error('[CHAT_EXPORT] Failed to export chat:', e);
    return { ok: false, reason: 'failed' };
  }
}
